import React, { useEffect, useMemo, useRef, useState } from "react";
import { DownloadSimple, VideoCamera, ShareNetwork, CircleNotch, Package, Fire } from "@phosphor-icons/react";
import { toast } from "sonner";
import { downloadPNG, downloadSVG, copyToClipboard } from "@/lib/download";
import { REEL_FORMATS } from "./formats";
import { fetchCurrentSponsor } from "./sponsors";
import { animateReel, countUpUser, countUpCommunity } from "./animateReel";
import { getTierConfig } from "@/components/dashboard/FoundingBadge";
import { api, endpoints } from "@/lib/api";

const triggerBlobDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 500);
};

const logDownload = (format_id, kind, user_id) => {
    api.post(endpoints.logDownload(), { format_id, kind, user_id }).catch(() => {});
};

const buildProps = ({ format, user, cfg, number, sponsor, community }) => {
    if (format.kind === "community") {
        return {
            community,
            color: cfg.color,
            sponsor,
        };
    }
    return {
        user,
        color: cfg.color,
        tierShort: cfg.short.toUpperCase(),
        tierCap: cfg.cap.toUpperCase(),
        memberNumber: number,
        sponsor,
    };
};

const ReelPreview = ({ format, svgHtml, previewRef }) => (
    <div
        ref={previewRef}
        data-testid={`reel-preview-${format.id}`}
        className={`w-full overflow-hidden rounded-md border border-[#1f1f22] bg-black ${format.aspectRatioClass} [&>svg]:block [&>svg]:h-full [&>svg]:w-full`}
        dangerouslySetInnerHTML={{ __html: svgHtml }}
    />
);

const ReelCard = ({ format, user, cfg, number, sponsor, community, sponsorInclude, isTop, downloadCount }) => {
    const previewRef = useRef(null);
    const [animating, setAnimating] = useState(false);
    const [animateProgress, setAnimateProgress] = useState(0);

    const activeSponsor = sponsorInclude ? sponsor : null;
    const props = useMemo(
        () => buildProps({ format, user, cfg, number, sponsor: activeSponsor, community }),
        [format, user, cfg, number, activeSponsor, community]
    );
    const svgHtml = useMemo(() => {
        if (format.kind === "community" && !community) return "";
        if (format.kind === "member" && !user) return "";
        try {
            return format.builder(props);
        } catch (e) {
            return "";
        }
    }, [format, props, user, community]);

    const getSvg = () => previewRef.current?.querySelector("svg");
    const filenameBase = `eviq-${format.id}-${number || "community"}`;
    const userId = user?.user_id || "demo-user";

    const handlePNG = async () => {
        await downloadPNG(getSvg(), `${filenameBase}.png`, {
            width: format.width,
            height: format.height,
            scale: 2,
        });
        logDownload(format.id, "png", userId);
        toast.success("PNG saved", {
            description: `${format.width * 2} × ${format.height * 2} · ${format.label}`,
        });
    };

    const handleSVG = () => {
        downloadSVG(getSvg(), `${filenameBase}.svg`);
        logDownload(format.id, "svg", userId);
        toast.success("SVG saved");
    };

    const handleCopyCaption = async () => {
        const caption = format.captionTemplate(user || {}, cfg, number);
        const ok = await copyToClipboard(caption);
        toast[ok ? "success" : "error"](ok ? "Caption copied" : "Could not copy");
    };

    const handleAnimate = async () => {
        if (animating) return;
        setAnimating(true);
        setAnimateProgress(0);
        try {
            const animate =
                format.kind === "community" ? countUpCommunity(community) : countUpUser(user);
            const blob = await animateReel({
                builder: (frameProps) => format.builder({ ...props, ...frameProps }),
                width: format.width,
                height: format.height,
                duration_ms: 3500,
                fps: 30,
                animate,
                scale: 1,
                onProgress: setAnimateProgress,
            });
            const ext = blob.type.includes("mp4") ? "mp4" : "webm";
            triggerBlobDownload(blob, `${filenameBase}.${ext}`);
            logDownload(format.id, ext, userId);
            toast.success("Animated reel saved", {
                description: `${format.width}×${format.height} · uploads to Instagram Reels, TikTok, X, Shorts`,
            });
        } catch (e) {
            toast.error("Animated reel failed", { description: e?.message || "" });
        } finally {
            setAnimating(false);
            setAnimateProgress(0);
        }
    };

    return (
        <div
            data-testid={`reel-card-${format.id}`}
            className={`flex flex-col gap-4 rounded-lg border p-5 ${
                isTop ? "border-[#a3e635]/40 bg-[#0a0f05]" : "border-[#1f1f22] bg-[#0a0a0a]"
            }`}
        >
            <div className="flex items-start justify-between gap-3">
                <div>
                    <span className="font-mono-metric text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                        {format.size}
                    </span>
                    <h4 className="mt-1 font-editorial text-lg leading-tight text-white">
                        {format.label}
                    </h4>
                </div>
                <div className="flex flex-col items-end gap-1">
                    {format.kind === "community" && <span className="eviq-chip">Community</span>}
                    {isTop && downloadCount > 0 && (
                        <span
                            data-testid={`reel-top-${format.id}`}
                            className="inline-flex items-center gap-1 rounded-full bg-[#a3e635] px-2 py-0.5 font-mono-metric text-[9px] uppercase tracking-[0.14em] text-black"
                        >
                            <Fire size={10} weight="fill" />
                            Most downloaded
                        </span>
                    )}
                    {downloadCount > 0 && !isTop && (
                        <span className="font-mono-metric text-[10px] uppercase tracking-[0.14em] text-zinc-500">
                            {downloadCount} downloads
                        </span>
                    )}
                </div>
            </div>

            <div className="mx-auto w-full max-w-[220px] sm:max-w-[240px]">
                <ReelPreview format={format} svgHtml={svgHtml} previewRef={previewRef} />
            </div>

            <div className="flex flex-wrap gap-2">
                <button
                    type="button"
                    data-testid={`reel-png-${format.id}`}
                    onClick={handlePNG}
                    className="inline-flex items-center gap-2 rounded-full bg-[#a3e635] px-3 py-1.5 text-xs font-medium text-black transition-transform duration-200 hover:-translate-y-0.5"
                >
                    <DownloadSimple size={12} weight="bold" />
                    PNG
                </button>
                <button
                    type="button"
                    data-testid={`reel-svg-${format.id}`}
                    onClick={handleSVG}
                    className="inline-flex items-center gap-2 rounded-full border border-[#27272a] bg-[#0a0a0a] px-3 py-1.5 text-xs text-zinc-200 transition-colors duration-200 hover:border-[#3a3a3f] hover:text-white"
                >
                    SVG
                </button>
                <button
                    type="button"
                    data-testid={`reel-animate-${format.id}`}
                    onClick={handleAnimate}
                    disabled={animating}
                    className="inline-flex items-center gap-2 rounded-full border border-[#27272a] bg-[#0a0a0a] px-3 py-1.5 text-xs text-zinc-200 transition-colors duration-200 hover:border-[#3a3a3f] hover:text-white disabled:opacity-60"
                >
                    {animating ? (
                        <>
                            <CircleNotch size={12} className="animate-spin" />
                            {Math.round(animateProgress * 100)}%
                        </>
                    ) : (
                        <>
                            <VideoCamera size={12} weight="fill" />
                            Animated
                        </>
                    )}
                </button>
                {format.kind === "member" && (
                    <button
                        type="button"
                        data-testid={`reel-caption-${format.id}`}
                        onClick={handleCopyCaption}
                        className="inline-flex items-center gap-2 rounded-full border border-[#27272a] bg-[#0a0a0a] px-3 py-1.5 text-xs text-zinc-200 transition-colors duration-200 hover:border-[#3a3a3f] hover:text-white"
                    >
                        Caption
                    </button>
                )}
            </div>
            <div className="flex flex-wrap gap-1.5 pt-1">
                {format.tags.map((t) => (
                    <span
                        key={t}
                        className="rounded-full border border-[#1f1f22] bg-[#050505] px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] text-zinc-500"
                    >
                        {t}
                    </span>
                ))}
            </div>
        </div>
    );
};

export const ReelKit = ({ user }) => {
    const cfg = getTierConfig(user?.founder_tier);
    const number = user ? String(user.member_number).padStart(4, "0") : "0000";

    const [sponsor, setSponsor] = useState(null);
    const [sponsorInclude, setSponsorInclude] = useState(true);
    const [community, setCommunity] = useState(null);
    const [downloadCounts, setDownloadCounts] = useState({});

    useEffect(() => {
        let ignore = false;
        (async () => {
            const [sp, comm, counts] = await Promise.all([
                fetchCurrentSponsor(),
                api.get(endpoints.community()).then((r) => r.data).catch(() => null),
                api.get(endpoints.downloads()).then((r) => r.data).catch(() => ({ by_format: [] })),
            ]);
            if (ignore) return;
            setSponsor(sp);
            setCommunity(comm);
            const map = {};
            for (const d of counts.by_format || []) map[d.format_id] = d.count || 0;
            setDownloadCounts(map);
        })();
        return () => {
            ignore = true;
        };
    }, []);

    // Order by download count (desc), stable fallback to registry order.
    const orderedFormats = useMemo(() => {
        return [...REEL_FORMATS].sort((a, b) => {
            const ca = downloadCounts[a.id] || 0;
            const cb = downloadCounts[b.id] || 0;
            if (cb !== ca) return cb - ca;
            return REEL_FORMATS.indexOf(a) - REEL_FORMATS.indexOf(b);
        });
    }, [downloadCounts]);

    const topId = orderedFormats[0]?.id;

    if (!user) return null;

    return (
        <div
            data-testid="reel-kit"
            className="rounded-lg border border-[#1f1f22] bg-[#0a0a0a] p-6 sm:p-8"
        >
            <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div>
                    <div className="flex items-center gap-2 text-zinc-500">
                        <ShareNetwork size={14} weight="fill" color={cfg.color} />
                        <span className="font-mono-metric text-[10px] uppercase tracking-[0.22em]">
                            Social media kit · Reel formats
                        </span>
                    </div>
                    <h3 className="mt-3 font-editorial text-xl leading-tight text-white sm:text-2xl">
                        Turn your impact into a reel.
                    </h3>
                    <p className="mt-2 max-w-xl text-sm text-zinc-500">
                        Every format sized correctly for its platform. Download static (PNG/SVG)
                        or an animated reel (WebM) that uploads directly to Instagram Reels,
                        TikTok, YouTube Shorts, and X.
                    </p>
                </div>
                <label
                    className="flex cursor-pointer items-center gap-3 rounded-full border border-[#27272a] bg-[#050505] px-4 py-2"
                    data-testid="reel-sponsor-toggle"
                >
                    <Package size={14} color={sponsor ? cfg.color : "#52525b"} weight="fill" />
                    <span className="flex flex-col leading-tight">
                        <span className="font-mono-metric text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                            Include sponsor
                        </span>
                        <span className="text-xs text-zinc-200">
                            {sponsor ? sponsor.name : "None this month"}
                        </span>
                    </span>
                    <input
                        type="checkbox"
                        checked={sponsorInclude}
                        onChange={(e) => setSponsorInclude(e.target.checked)}
                        disabled={!sponsor}
                        className="h-4 w-4 accent-[#a3e635]"
                    />
                </label>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {orderedFormats.map((format) => (
                    <ReelCard
                        key={format.id}
                        format={format}
                        user={user}
                        cfg={cfg}
                        number={number}
                        sponsor={sponsor}
                        community={community}
                        sponsorInclude={sponsorInclude}
                        isTop={format.id === topId}
                        downloadCount={downloadCounts[format.id] || 0}
                    />
                ))}
            </div>
        </div>
    );
};

export default ReelKit;
